import React, {useEffect} from 'react';
import {View, Text, Platform} from 'react-native';
import Svg, {Circle} from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedProps,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import {useStyle} from 'src/providers/style';
import {Colors, Typography} from 'src/styles';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const CIRCUMFERENCE = 2 * Math.PI * 52;

interface Props {
  progress: number;
  variant?: 'circular' | 'linear';
}

const CircularProgress = ({progress, variant = 'circular'}: Props) => {
  const styles = useStyle();
  const animatedProgress = useSharedValue(progress);

  useEffect(() => {
    animatedProgress.value = withTiming(progress, {duration: 400});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress]);

  const animatedBarStyle = useAnimatedStyle(() => ({
    width: `${animatedProgress.value * 100}%`,
  }));

  const animatedProps = useAnimatedProps(() => ({
    strokeDashoffset: CIRCUMFERENCE * (1 - animatedProgress.value),
  }));

  if (variant === 'linear') {
    return (
      <View style={[styles.justifyCenter, styles.alignCenter]}>
        <View
          style={[
            styles.height40,
            styles.width300,
            styles.backgroundColorGray300,
            styles.overflowHidden,
            styles.rounded,
          ]}>
          <Animated.View
            style={[
              styles.height40,
              animatedBarStyle,
              glowingStyle,
              styles.backgroundColorTeal600,
            ]}
          />
        </View>
        <Text style={[Typography.P3_BOLD]}>
          {`${Math.round(progress * 100)}%`}
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.justifyCenter, styles.alignCenter]}>
      <Svg width={120} height={120}>
        <Circle
          cx={60}
          cy={60}
          r={52}
          stroke={Colors.GRAY_300}
          strokeWidth={15}
          fill="white"
        />
        <AnimatedCircle
          cx={60}
          cy={60}
          r={52}
          stroke={Colors.TEAL_600}
          strokeWidth={15}
          strokeDasharray={`${CIRCUMFERENCE}, ${CIRCUMFERENCE}`}
          animatedProps={animatedProps}
          strokeLinecap="butt"
          fill="none"
        />
      </Svg>
      <View style={[styles.positionAbsolute]}>
        <Text style={[Typography.P3_BOLD]}>
          {`${Math.round(progress * 100)}%`}
        </Text>
      </View>
    </View>
  );
};

export default CircularProgress;

const glowingStyle = Platform.select({
  ios: {
    shadowColor: Colors.TEAL_600,
    shadowOffset: {width: 0, height: 0},
    shadowOpacity: 0.5,
    shadowRadius: 8,
  },
  android: {
    elevation: 6,
  },
  web: {
    boxShadow: `0 0 5px ${Colors.TEAL_600}`,
  },
});
